// Firing Fee Calculator
//
// The checkout API (create-and-checkout) is external and its contract is fixed:
// it receives { products: [{ title, price, weight, firingType, customerType, shelfNumber }], quantities: [] }
// and returns { success, checkout: { url } }. Nothing here changes that payload.

(function () {
  "use strict";

  // ==================== API CONFIGURATION ====================
  // Switch between 'dev' and 'prod' environments easily
  const API_CONFIG = {
    environment: "prod", // Change to 'dev' for local testing
    endpoints: {
      dev: "https://108828c8-d3a6-4b1b-97e5-5085448b5c06-00-28ev2ruihx5v8.janeway.replit.dev",
      prod: "https://throwing-shapes-new-api-1.replit.app",
    },
    getBaseUrl() {
      return this.endpoints[this.environment];
    },
    getCreateAndCheckoutUrl() {
      return `${this.getBaseUrl()}/api/shopify/create-and-checkout`;
    },
  };
  // ==========================================================

  // The API creates one Shopify product per piece and has no idempotency key, so a
  // blind retry can double-charge. Everything below is tuned around that: retry only
  // when the server explicitly rejected the request (429), and make the operator
  // confirm anything else.
  const REQUEST_TIMEOUT_MS = 60000;
  const MAX_RATE_LIMIT_RETRIES = 3;
  const DRAFT_TTL_MS = 12 * 60 * 60 * 1000;
  const DUPLICATE_WINDOW_MS = 15 * 60 * 1000;
  const MAX_WEIGHT_G = 50000;

  const storage = {
    read(key) {
      try {
        const raw = window.sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },
    write(key, value) {
      try {
        window.sessionStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* private mode / quota — the calculator still works, just without recovery */
      }
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (e) {
        /* no-op */
      }
    },
  };

  function toNumber(value, fallback) {
    const parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : fallback;
  }

  function formatMoney(amountInCents) {
    return `€${(amountInCents / 100).toFixed(2)}`;
  }

  function fingerprint(products) {
    const source = JSON.stringify(products);
    let hash = 0;
    for (let i = 0; i < source.length; i++) {
      hash = (hash << 5) - hash + source.charCodeAt(i);
      hash |= 0;
    }
    return `${products.length}:${hash}`;
  }

  // The API is trusted to return a Shopify checkout URL. Verify it before navigating
  // so a compromised or misconfigured endpoint cannot bounce customers somewhere else.
  function isSafeCheckoutUrl(url) {
    let parsed;
    try {
      parsed = new URL(url, window.location.origin);
    } catch (e) {
      return false;
    }
    if (parsed.protocol !== "https:") return false;
    const host = parsed.hostname.toLowerCase();
    return (
      host === window.location.hostname.toLowerCase() ||
      host.endsWith(".myshopify.com") ||
      host === "shopify.com" ||
      host.endsWith(".shopify.com")
    );
  }

  function initCalculator(root) {
    if (root.dataset.ffcInitialised === "true") return;
    root.dataset.ffcInitialised = "true";

    const sectionId = root.dataset.sectionId || "default";
    const DRAFT_KEY = `ffc:draft:${sectionId}`;
    const SUBMIT_KEY = `ffc:lastSubmit:${sectionId}`;

    const config = {
      memberRate: toNumber(root.dataset.memberRate, 3),
      guestRate: toNumber(root.dataset.guestRate, 8),
      rates: {
        member_bisque: toNumber(root.dataset.rateMemberBisque, 0),
        member_glaze: toNumber(root.dataset.rateMemberGlaze, 0),
        guest_bisque: toNumber(root.dataset.rateGuestBisque, 0),
        guest_glaze: toNumber(root.dataset.rateGuestGlaze, 0),
      },
      minCharge: Math.max(0, toNumber(root.dataset.minCharge, 0)),
      maxPieces: Math.max(1, toNumber(root.dataset.maxPieces, 50)),
      confirmThreshold: Math.max(0, toNumber(root.dataset.confirmThreshold, 0)),
      autoLogout: root.dataset.autoLogout === "true",
      logoutTimeoutMs: Math.max(1, toNumber(root.dataset.logoutTimeout, 3)) * 60 * 1000,
      customerLoggedIn: root.dataset.customerLoggedIn === "true",
    };

    const el = (name) => root.querySelector(`[data-ffc="${name}"]`);
    const els = (name) => Array.from(root.querySelectorAll(`[data-ffc="${name}"]`));

    const elements = {
      form: el("form"),
      customerName: el("customer-name"),
      shelfNumber: el("shelf-number"),
      pieceName: el("piece-name"),
      pieceWeight: el("piece-weight"),
      weightUnit: el("weight-unit"),
      weightHint: el("weight-hint"),
      calculatedCost: el("calculated-cost"),
      piecesList: el("pieces-list"),
      piecesContainer: el("pieces-container"),
      orderSummary: el("order-summary"),
      totalPieces: el("total-pieces"),
      totalWeight: el("total-weight"),
      totalCost: el("total-cost"),
      checkoutBtn: el("checkout"),
      cartText: el("cart-text"),
      cartLoading: el("cart-loading"),
      errorMessage: el("error"),
      successMessage: el("success"),
      addPieceText: el("add-piece-text"),
      cancelEdit: el("cancel-edit"),
      restoreNotice: el("restore-notice"),
      restoreText: el("restore-text"),
      discardDraft: el("discard-draft"),
      resetBtn: el("reset"),
      countdown: el("countdown"),
    };

    if (!elements.form) return;

    const state = {
      pieces: [],
      editingIndex: -1,
      submitting: false,
      redirecting: false,
    };

    let errorTimer = null;
    let successTimer = null;

    // ---------------------------------------------------------------- pricing

    const PricingCalculator = {
      rateFor(customerType, firingType) {
        const override = config.rates[`${customerType}_${firingType}`];
        if (override > 0) return override;
        return customerType === "member" ? config.memberRate : config.guestRate;
      },

      // Returns whole cents so repeated addition cannot drift.
      costInCents(weight, unit, customerType, firingType) {
        const weightInKg = unit === "kg" ? weight : weight / 1000;
        const rate = this.rateFor(customerType, firingType);
        const cents = Math.round(weightInKg * rate * 100);
        return Math.max(cents, Math.round(config.minCharge * 100));
      },
    };

    function weightInGrams(piece) {
      return piece.unit === "kg" ? piece.weight * 1000 : piece.weight;
    }

    function maxWeightForUnit(unit) {
      return unit === "kg" ? MAX_WEIGHT_G / 1000 : MAX_WEIGHT_G;
    }

    // ---------------------------------------------------------------- messages

    function showError(message) {
      elements.errorMessage.textContent = message;
      elements.errorMessage.style.display = "block";
      elements.successMessage.style.display = "none";
      window.clearTimeout(errorTimer);
      errorTimer = window.setTimeout(() => {
        elements.errorMessage.style.display = "none";
      }, 8000);
    }

    function showSuccess(message) {
      elements.successMessage.textContent = message;
      elements.successMessage.style.display = "block";
      elements.errorMessage.style.display = "none";
      window.clearTimeout(successTimer);
      successTimer = window.setTimeout(() => {
        elements.successMessage.style.display = "none";
      }, 5000);
    }

    // ---------------------------------------------------------------- overlay

    const OverlayManager = {
      overlay: null,
      messageEl: null,

      create() {
        if (this.overlay) return;

        const overlay = document.createElement("div");
        overlay.className = "ffc-overlay";
        overlay.setAttribute("role", "dialog");
        overlay.setAttribute("aria-modal", "true");

        const content = document.createElement("div");
        content.className = "ffc-overlay__content";

        const spinner = document.createElement("div");
        spinner.className = "ffc-overlay__spinner";

        const title = document.createElement("h3");
        title.className = "ffc-overlay__title";
        title.textContent = "Creating Your Checkout...";

        const message = document.createElement("p");
        message.className = "ffc-overlay__message";
        message.textContent =
          "Please wait while we prepare your firing fees for checkout. This may take a few moments.";

        content.appendChild(spinner);
        content.appendChild(title);
        content.appendChild(message);
        overlay.appendChild(content);
        document.body.appendChild(overlay);

        this.overlay = overlay;
        this.contentEl = content;
        this.messageEl = message;
      },

      setMessage(message) {
        if (this.messageEl) this.messageEl.textContent = message;
      },

      // `message` can originate from the API, so it is only ever set as text.
      showError(message, options) {
        const opts = options || {};
        if (!this.overlay) this.create();
        const content = this.contentEl;
        content.textContent = "";

        const icon = document.createElement("div");
        icon.className = "ffc-overlay__icon";
        icon.textContent = opts.warning ? "⏳" : "⚠️";

        const title = document.createElement("h3");
        title.className = "ffc-overlay__title ffc-overlay__title--error";
        title.textContent = opts.title || "Error";

        const body = document.createElement("p");
        body.className = "ffc-overlay__message";
        body.textContent = message;

        const actions = document.createElement("div");
        actions.className = "ffc-overlay__actions";

        if (opts.onRetry) {
          const retry = document.createElement("button");
          retry.type = "button";
          retry.className = "btn-primary";
          retry.textContent = opts.retryLabel || "Try again";
          retry.addEventListener("click", () => {
            this.remove();
            opts.onRetry();
          });
          actions.appendChild(retry);
        }

        const close = document.createElement("button");
        close.type = "button";
        close.className = "btn-secondary";
        close.textContent = "Close";
        close.addEventListener("click", () => this.remove());
        actions.appendChild(close);

        content.appendChild(icon);
        content.appendChild(title);
        content.appendChild(body);
        content.appendChild(actions);
      },

      remove() {
        if (this.overlay) {
          this.overlay.remove();
          this.overlay = null;
          this.contentEl = null;
          this.messageEl = null;
        }
      },
    };

    // ---------------------------------------------------------------- draft persistence

    function saveDraft() {
      if (state.redirecting) return;
      if (!state.pieces.length) {
        storage.remove(DRAFT_KEY);
        return;
      }
      storage.write(DRAFT_KEY, {
        savedAt: new Date().getTime(),
        pieces: state.pieces,
        customerName: elements.customerName.value,
        shelfNumber: elements.shelfNumber.value,
      });
    }

    function clearDraft() {
      storage.remove(DRAFT_KEY);
    }

    function restoreDraft() {
      const draft = storage.read(DRAFT_KEY);
      if (!draft || !Array.isArray(draft.pieces) || !draft.pieces.length) return;

      if (new Date().getTime() - (draft.savedAt || 0) > DRAFT_TTL_MS) {
        clearDraft();
        return;
      }

      const pieces = draft.pieces.filter(
        (piece) =>
          piece &&
          typeof piece.name === "string" &&
          typeof piece.costInCents === "number" &&
          isFinite(piece.costInCents) &&
          isFinite(piece.weight)
      );
      if (!pieces.length) {
        clearDraft();
        return;
      }

      state.pieces = pieces;
      if (draft.customerName) elements.customerName.value = draft.customerName;
      if (draft.shelfNumber) elements.shelfNumber.value = draft.shelfNumber;

      PieceManager.updateDisplay();

      elements.restoreText.textContent = `Recovered ${pieces.length} unfinished piece${
        pieces.length === 1 ? "" : "s"
      } from this device.`;
      elements.restoreNotice.hidden = false;
    }

    // ---------------------------------------------------------------- pieces

    const PieceManager = {
      addPiece(pieceData) {
        if (state.editingIndex >= 0) {
          state.pieces[state.editingIndex] = pieceData;
          this.stopEditing();
        } else {
          state.pieces.push(pieceData);
        }
        this.updateDisplay();
        this.clearForm();
        saveDraft();
      },

      removePiece(index) {
        state.pieces.splice(index, 1);
        if (state.editingIndex === index) {
          this.stopEditing();
          this.clearForm();
        } else if (state.editingIndex > index) {
          state.editingIndex -= 1;
        }
        this.updateDisplay();
        saveDraft();
      },

      editPiece(index) {
        const piece = state.pieces[index];
        if (!piece) return;
        state.editingIndex = index;

        elements.pieceName.value = piece.name;
        elements.pieceWeight.value = piece.weight;
        elements.weightUnit.value = piece.unit;
        // Editing restores the name and shelf the piece was created with, so correcting
        // an earlier entry cannot silently reassign it to whoever is at the terminal now.
        elements.customerName.value = piece.customerName;
        elements.shelfNumber.value = piece.shelfNumber;
        syncWeightConstraints();

        setRadio("firing-type", piece.firingType);
        setRadio("customer-type", piece.customerType);

        elements.addPieceText.textContent = "Update Piece";
        elements.cancelEdit.hidden = false;
        this.calculateRealTime();

        elements.form.scrollIntoView({ behavior: "smooth", block: "center" });
      },

      stopEditing() {
        state.editingIndex = -1;
        elements.addPieceText.textContent = "Add Piece";
        elements.cancelEdit.hidden = true;
      },

      updateDisplay() {
        const hasPieces = state.pieces.length > 0;
        elements.piecesList.hidden = !hasPieces;
        elements.orderSummary.hidden = !hasPieces;
        elements.piecesContainer.textContent = "";

        if (!hasPieces) {
          elements.totalPieces.textContent = "0";
          elements.totalWeight.textContent = "0g";
          elements.totalCost.textContent = "€0.00";
          return;
        }

        state.pieces.forEach((piece, index) => {
          const item = document.createElement("div");
          item.className = "piece-item";

          const details = document.createElement("div");
          details.className = "piece-details";

          const name = document.createElement("div");
          name.className = "piece-name";
          name.textContent = `${piece.name} — ${piece.customerName} (Shelf ${piece.shelfNumber})`;

          const info = document.createElement("div");
          info.className = "piece-info";
          info.textContent = `${piece.weight}${piece.unit}, ${piece.firingType}, ${
            piece.customerType
          } — ${formatMoney(piece.costInCents)}`;

          details.appendChild(name);
          details.appendChild(info);

          const actions = document.createElement("div");
          actions.className = "piece-actions";

          const edit = document.createElement("button");
          edit.type = "button";
          edit.className = "btn-secondary";
          edit.textContent = "Edit";
          edit.addEventListener("click", () => this.editPiece(index));

          const remove = document.createElement("button");
          remove.type = "button";
          remove.className = "btn-secondary";
          remove.textContent = "Remove";
          remove.addEventListener("click", () => this.removePiece(index));

          actions.appendChild(edit);
          actions.appendChild(remove);

          item.appendChild(details);
          item.appendChild(actions);
          elements.piecesContainer.appendChild(item);
        });

        const totalWeightG = state.pieces.reduce(
          (sum, piece) => sum + weightInGrams(piece),
          0
        );
        const totalCents = state.pieces.reduce(
          (sum, piece) => sum + piece.costInCents,
          0
        );

        elements.totalPieces.textContent = String(state.pieces.length);
        elements.totalWeight.textContent =
          totalWeightG >= 1000
            ? `${(totalWeightG / 1000).toFixed(2)}kg`
            : `${totalWeightG}g`;
        elements.totalCost.textContent = formatMoney(totalCents);
      },

      clearForm() {
        elements.form.reset();
        elements.calculatedCost.textContent = "Cost: €0.00";
        this.stopEditing();
        syncWeightConstraints();
        root
          .querySelectorAll(".radio-option")
          .forEach((option) => option.classList.remove("selected"));
      },

      calculateRealTime() {
        const weight = parseFloat(elements.pieceWeight.value) || 0;
        const unit = elements.weightUnit.value;
        const customerType = checkedValue("customer-type");
        const firingType = checkedValue("firing-type");

        if (weight > 0 && customerType && firingType) {
          const cents = PricingCalculator.costInCents(
            weight,
            unit,
            customerType,
            firingType
          );
          elements.calculatedCost.textContent = `Cost: ${formatMoney(cents)}`;
        } else {
          elements.calculatedCost.textContent = "Cost: €0.00";
        }
      },
    };

    function checkedValue(name) {
      const checked = els(name).find((input) => input.checked);
      return checked ? checked.value : null;
    }

    function setRadio(name, value) {
      els(name).forEach((input) => {
        const selected = input.value === value;
        input.checked = selected;
        const option = input.closest(".radio-option");
        if (option) option.classList.toggle("selected", selected);
      });
    }

    // Grams and kilograms need different min/step, otherwise the browser silently
    // rejects fractional kilograms and anything under 1kg.
    function syncWeightConstraints() {
      const unit = elements.weightUnit.value;
      if (unit === "kg") {
        elements.pieceWeight.min = "0.001";
        elements.pieceWeight.step = "any";
        elements.pieceWeight.max = "50";
        elements.weightHint.textContent = "0.001kg – 50kg";
      } else {
        elements.pieceWeight.min = "1";
        elements.pieceWeight.step = "1";
        elements.pieceWeight.max = String(MAX_WEIGHT_G);
        elements.weightHint.textContent = "1g – 50,000g";
      }
    }

    // ---------------------------------------------------------------- checkout

    const CartManager = {
      buildProducts() {
        return state.pieces.map((piece) => ({
          title: `${piece.customerName} - ${piece.name}`,
          price: (piece.costInCents / 100).toFixed(2),
          weight: `${piece.weight}${piece.unit}`,
          firingType: piece.firingType,
          customerType: piece.customerType,
          shelfNumber: piece.shelfNumber,
        }));
      },

      recentDuplicate(key) {
        const last = storage.read(SUBMIT_KEY);
        if (!last || last.key !== key) return false;
        return new Date().getTime() - last.at < DUPLICATE_WINDOW_MS;
      },

      rememberSubmission(key) {
        storage.write(SUBMIT_KEY, { key: key, at: new Date().getTime() });
      },

      setBusy(busy) {
        state.submitting = busy;
        elements.checkoutBtn.disabled = busy;
        elements.cartText.textContent = busy ? "Creating checkout…" : "Add All to Cart";
        elements.cartLoading.style.display = busy ? "block" : "none";
      },

      async post(products, quantities, signal) {
        let attempt = 0;

        while (true) {
          const response = await fetch(API_CONFIG.getCreateAndCheckoutUrl(), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ products: products, quantities: quantities }),
            signal: signal,
          });

          // 429 means the request was rejected outright, so retrying cannot
          // duplicate anything. Every other status may have been processed.
          if (response.status === 429 && attempt < MAX_RATE_LIMIT_RETRIES) {
            const retryAfter = toNumber(response.headers.get("Retry-After"), 0);
            const waitMs = retryAfter > 0 ? retryAfter * 1000 : Math.pow(2, attempt) * 1000;
            attempt += 1;
            OverlayManager.setMessage(
              `The studio checkout service is busy. Retrying (${attempt} of ${MAX_RATE_LIMIT_RETRIES})…`
            );
            await new Promise((resolve) => window.setTimeout(resolve, waitMs));
            continue;
          }

          let data = null;
          try {
            data = await response.json();
          } catch (e) {
            data = null;
          }

          if (!response.ok) {
            const detail = data && data.message ? data.message : response.statusText;
            const error = new Error(
              `Server error (${response.status}${detail ? `: ${detail}` : ""}). Please try again or contact support.`
            );
            error.serverRejected = response.status >= 400 && response.status < 500;
            throw error;
          }

          return data;
        }
      },

      async createAndCheckout() {
        if (state.submitting) return;

        if (!state.pieces.length) {
          showError("No pieces to add to cart");
          return;
        }

        if (state.editingIndex >= 0) {
          showError("Finish editing the current piece before checking out.");
          return;
        }

        const names = new Set(state.pieces.map((piece) => piece.customerName));
        if (names.size > 1) {
          const proceed = window.confirm(
            `These pieces are for ${names.size} different customers (${Array.from(names).join(
              ", "
            )}). They will all go onto one order and one payment. Continue?`
          );
          if (!proceed) return;
        }

        const products = this.buildProducts();
        const quantities = new Array(state.pieces.length).fill(1);
        const key = fingerprint(products);

        if (this.recentDuplicate(key)) {
          const proceed = window.confirm(
            "An identical checkout was created from this device in the last 15 minutes. " +
              "Sending it again will create a second set of firing fees and a second payment. Continue anyway?"
          );
          if (!proceed) return;
        }

        this.setBusy(true);
        IdleTimer.pause();
        OverlayManager.create();
        OverlayManager.setMessage(
          `Creating ${state.pieces.length} firing fee${
            state.pieces.length > 1 ? "s" : ""
          } and preparing checkout...`
        );

        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

        try {
          const data = await this.post(products, quantities, controller.signal);

          if (!data || !data.success) {
            const error = new Error(
              (data && data.message) || "Failed to create checkout"
            );
            error.serverRejected = true;
            throw error;
          }

          const url = data.checkout && data.checkout.url;
          if (!url || !isSafeCheckoutUrl(url)) {
            // The fees exist at this point, so the draft must not be thrown away.
            throw new Error(
              "The firing fees were created but no valid checkout link came back. " +
                "Please ask studio staff to check the order before trying again."
            );
          }

          this.rememberSubmission(key);
          OverlayManager.setMessage("Redirecting to checkout...");

          // Only discard the saved work once the browser has actually left the page.
          state.redirecting = true;
          window.addEventListener(
            "pagehide",
            () => {
              if (state.redirecting) clearDraft();
            },
            { once: true }
          );
          window.location.href = url;
        } catch (error) {
          state.redirecting = false;

          if (error.name === "AbortError") {
            OverlayManager.showError(
              "The checkout is taking longer than expected. It may still have been created — " +
                "please check with studio staff before retrying, so nobody is charged twice.",
              {
                title: "Still processing",
                warning: true,
                retryLabel: "Retry anyway",
                onRetry: () => this.createAndCheckout(),
              }
            );
          } else if (error instanceof TypeError) {
            // The connection dropped, so it is unknown whether the request reached
            // the API. Warn before retrying rather than risk a second charge.
            OverlayManager.showError(
              "Network error — the connection dropped before we got an answer. The fees may " +
                "still have been created, so check with studio staff before retrying.",
              {
                title: "Connection lost",
                warning: true,
                retryLabel: "Retry anyway",
                onRetry: () => this.createAndCheckout(),
              }
            );
          } else {
            OverlayManager.showError(
              error.message || "Error creating checkout. Please try again.",
              // Only offer a one-click retry when the server explicitly rejected the
              // request; otherwise the fees may already exist.
              error.serverRejected ? { onRetry: () => this.createAndCheckout() } : {}
            );
          }
        } finally {
          window.clearTimeout(timeout);
          this.setBusy(false);
          IdleTimer.resume();
        }
      },

      clearAll() {
        state.pieces = [];
        PieceManager.updateDisplay();
        PieceManager.clearForm();
        elements.customerName.value = "";
        elements.shelfNumber.value = "";
        elements.restoreNotice.hidden = true;
        clearDraft();
      },
    };

    // ---------------------------------------------------------------- idle timer

    const IdleTimer = {
      intervalId: null,
      warningEl: null,
      remaining: config.logoutTimeoutMs / 1000,
      paused: false,

      start() {
        if (!config.autoLogout || !elements.countdown) return;
        this.reset();
        this.intervalId = window.setInterval(() => this.tick(), 1000);
      },

      tick() {
        if (this.paused) return;

        this.remaining -= 1;

        if (this.remaining <= 30 && !this.warningEl) this.showWarning();

        if (this.remaining <= 0) {
          this.expire();
          return;
        }

        const minutes = Math.floor(this.remaining / 60);
        const seconds = this.remaining % 60;
        elements.countdown.textContent = `${minutes}:${String(seconds).padStart(2, "0")}`;
        if (this.warningEl) {
          this.warningEl.querySelector("[data-ffc-warning-count]").textContent =
            String(this.remaining);
        }
      },

      showWarning() {
        const overlay = document.createElement("div");
        overlay.className = "ffc-overlay";

        const content = document.createElement("div");
        content.className = "ffc-overlay__content";

        const title = document.createElement("h3");
        title.className = "ffc-overlay__title";
        title.textContent = "Are you still there?";

        const message = document.createElement("p");
        message.className = "ffc-overlay__message";
        message.appendChild(document.createTextNode("This session clears in "));
        const count = document.createElement("strong");
        count.setAttribute("data-ffc-warning-count", "");
        count.textContent = String(Math.max(0, this.remaining));
        message.appendChild(count);
        message.appendChild(
          document.createTextNode(" seconds and the pieces entered will be removed.")
        );

        const button = document.createElement("button");
        button.type = "button";
        button.className = "btn-primary";
        button.textContent = "I'm still here";
        button.addEventListener("click", () => this.reset());

        content.appendChild(title);
        content.appendChild(message);
        content.appendChild(button);
        overlay.appendChild(content);
        document.body.appendChild(overlay);

        this.warningEl = overlay;
      },

      dismissWarning() {
        if (this.warningEl) {
          this.warningEl.remove();
          this.warningEl = null;
        }
      },

      reset() {
        this.remaining = config.logoutTimeoutMs / 1000;
        this.dismissWarning();
        if (elements.countdown) {
          const minutes = Math.floor(this.remaining / 60);
          const seconds = this.remaining % 60;
          elements.countdown.textContent = `${minutes}:${String(seconds).padStart(2, "0")}`;
        }
      },

      pause() {
        this.paused = true;
        this.dismissWarning();
      },

      resume() {
        this.paused = false;
        this.reset();
      },

      expire() {
        this.stop();
        CartManager.clearAll();
        if (config.customerLoggedIn) {
          window.location.href = "/account/logout";
          return;
        }
        showSuccess("Session cleared after inactivity.");
      },

      stop() {
        if (this.intervalId) {
          window.clearInterval(this.intervalId);
          this.intervalId = null;
        }
        this.dismissWarning();
      },
    };

    // ---------------------------------------------------------------- wiring

    elements.form.addEventListener("submit", function (event) {
      event.preventDefault();

      const customerName = elements.customerName.value.trim();
      const shelfNumber = elements.shelfNumber.value.trim();
      const pieceName = elements.pieceName.value.trim();
      const weight = parseFloat(elements.pieceWeight.value);
      const unit = elements.weightUnit.value;
      const firingType = checkedValue("firing-type");
      const customerType = checkedValue("customer-type");

      if (
        !customerName ||
        !shelfNumber ||
        !pieceName ||
        !isFinite(weight) ||
        !firingType ||
        !customerType
      ) {
        showError("Please fill in all required fields");
        return;
      }

      if (
        state.editingIndex < 0 &&
        state.pieces.length >= config.maxPieces
      ) {
        showError(
          `You can add up to ${config.maxPieces} pieces per checkout. Check these out first, then start a new batch.`
        );
        return;
      }

      const maxWeight = maxWeightForUnit(unit);
      if (weight <= 0 || weight > maxWeight) {
        showError(
          `Weight must be between ${unit === "kg" ? "0.001kg" : "1g"} and ${
            unit === "kg" ? "50kg" : "50,000g"
          }`
        );
        return;
      }

      const costInCents = PricingCalculator.costInCents(
        weight,
        unit,
        customerType,
        firingType
      );

      if (
        config.confirmThreshold > 0 &&
        costInCents > config.confirmThreshold * 100
      ) {
        const proceed = window.confirm(
          `That is ${formatMoney(costInCents)} for a single piece (${weight}${unit}). Is the weight and unit correct?`
        );
        if (!proceed) return;
      }

      PieceManager.addPiece({
        name: pieceName,
        customerName: customerName,
        shelfNumber: shelfNumber,
        weight: weight,
        unit: unit,
        firingType: firingType,
        customerType: customerType,
        costInCents: costInCents,
        dateCreated: new Date().toLocaleDateString("en-IE"),
      });

      elements.pieceName.focus();
    });

    elements.pieceWeight.addEventListener("input", () =>
      PieceManager.calculateRealTime()
    );

    elements.weightUnit.addEventListener("change", () => {
      syncWeightConstraints();
      PieceManager.calculateRealTime();
    });

    els("firing-type")
      .concat(els("customer-type"))
      .forEach((radio) => {
        radio.addEventListener("change", function () {
          setRadio(this.dataset.ffc, this.value);
          PieceManager.calculateRealTime();
        });
      });

    elements.checkoutBtn.addEventListener("click", () =>
      CartManager.createAndCheckout()
    );

    elements.cancelEdit.addEventListener("click", () => {
      PieceManager.clearForm();
    });

    elements.discardDraft.addEventListener("click", () => {
      if (window.confirm("Discard the recovered pieces?")) {
        CartManager.clearAll();
      }
    });

    elements.resetBtn.addEventListener("click", () => {
      if (
        state.pieces.length &&
        !window.confirm(
          `This clears ${state.pieces.length} piece${
            state.pieces.length === 1 ? "" : "s"
          } that have not been checked out. Continue?`
        )
      ) {
        return;
      }
      CartManager.clearAll();
      elements.customerName.focus();
    });

    // Keep the name and shelf on the draft so a refresh mid-queue loses nothing.
    [elements.customerName, elements.shelfNumber].forEach((input) => {
      input.addEventListener("change", saveDraft);
    });

    window.addEventListener("beforeunload", (event) => {
      if (state.redirecting || !state.pieces.length) return;
      event.preventDefault();
      event.returnValue = "";
    });

    if (config.autoLogout) {
      ["pointerdown", "keydown", "input", "change", "scroll", "touchstart", "focusin"].forEach(
        (eventType) => {
          root.addEventListener(eventType, () => IdleTimer.reset(), { passive: true });
        }
      );
      document.addEventListener("scroll", () => IdleTimer.reset(), { passive: true });
    }

    syncWeightConstraints();
    restoreDraft();
    IdleTimer.start();

    root.ffcTeardown = function () {
      IdleTimer.stop();
      OverlayManager.remove();
      window.clearTimeout(errorTimer);
      window.clearTimeout(successTimer);
    };
  }

  function initAll(scope) {
    (scope || document)
      .querySelectorAll("[data-firing-fee-calculator]")
      .forEach(initCalculator);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  // Theme editor support: sections can be re-rendered without a page load.
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-firing-fee-calculator]").forEach((root) => {
      if (typeof root.ffcTeardown === "function") root.ffcTeardown();
    });
  });
})();
